import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Store, CircleDollarSign } from "lucide-react";
import { api, peso } from "../lib/api";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

const FROZEN_SVG = (title, sub) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600' width='600' height='600'>
<defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'>
<stop offset='0' stop-color='#7FC4E0'/><stop offset='1' stop-color='#24526B'/>
</linearGradient></defs>
<rect width='600' height='600' fill='url(%23g)'/>
<g opacity='0.35' fill='#FFFFFF'>
<circle cx='120' cy='130' r='3'/><circle cx='480' cy='200' r='2.5'/><circle cx='250' cy='80' r='2'/>
<circle cx='420' cy='420' r='3'/><circle cx='160' cy='380' r='2'/><circle cx='540' cy='340' r='2.5'/>
<circle cx='80' cy='260' r='2'/><circle cx='320' cy='160' r='2'/>
</g>
<g transform='translate(300 270)' fill='#FFFFFF' opacity='0.85'>
<path d='M0 -90 L6 -70 L26 -70 L10 -55 L16 -35 L0 -47 L-16 -35 L-10 -55 L-26 -70 L-6 -70 Z'/>
<path d='M-55 0 L-45 -18 L-25 -18 L-41 -4 L-35 16 L-55 4 L-75 16 L-69 -4 L-85 -18 L-65 -18 Z' opacity='0.7'/>
<path d='M55 0 L65 -18 L85 -18 L69 -4 L75 16 L55 4 L35 16 L41 -4 L25 -18 L45 -18 Z' opacity='0.7'/>
</g>
<text x='300' y='440' text-anchor='middle' font-family='system-ui,sans-serif' font-weight='800' font-size='44' fill='#FFFFFF'>${title}</text>
<text x='300' y='478' text-anchor='middle' font-family='system-ui,sans-serif' font-weight='600' font-size='18' fill='#E8F4F9'>${sub}</text>
</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const CATEGORIES = [
  {
    key: "Meats",
    tagline: "Fresh pork, beef & chicken — same-day cuts",
    images: [
      "/assets/chicken-cuttings.png",
      "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=800&q=80",
      "https://images.unsplash.com/photo-1588168333986-5078d3ae3976?w=800&q=80",
    ],
  },
  {
    key: "Groceries",
    tagline: "Rice, canned goods & pantry essentials",
    images: [
      "https://images.unsplash.com/photo-1601598851547-4302969d0614?w=1200&q=80",
      "https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?w=800",
      "https://images.pexels.com/photos/3962286/pexels-photo-3962286.jpeg?w=800",
    ],
  },
  {
    key: "Condiments",
    tagline: "Sarsa, suka, toyo & more",
    images: [
      "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=800&q=80",
      "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=800&q=80",
      "https://images.unsplash.com/photo-1607301405390-d831c242f59b?w=800&q=80",
    ],
  },
  {
    key: "Frozen Products",
    tagline: "Hotdogs, nuggets, ice cream & more",
    images: [
      FROZEN_SVG("Frozen", "Meats & Seafood"),
      FROZEN_SVG("Ice Cream", "Desserts"),
      FROZEN_SVG("Nuggets", "Ready-to-Cook"),
    ],
  },
  {
    key: "Sunjin Feeds",
    tagline: "Premium hog, layer & duck feeds",
    images: [
      "/assets/sunjin/grower.png",
      "/assets/sunjin/preggy-meal.png",
      "/assets/sunjin/duck-layer.png",
    ],
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    // Lead with fresh meats — surface those first when visitors land on the home page
    api.get("/products", { params: { category: "Meats" } })
      .then((r) => setFeatured(r.data.slice(0, 8)))
      .catch(() => {});
  }, []);

  return (
    <div className="page-enter">
      <SEO
        title="Arbe Minimart — Fresh Meat & Grocery Delivery in Oroquieta City, Misamis Occidental"
        description="Order fresh pork, beef, chicken, groceries, condiments and frozen products online. Same-day delivery in Oroquieta City, Misamis Occidental. FREE delivery over ₱1,000. GCash, Maya, Bank Transfer or COD."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/hero-farm.png"
            alt="Arbe farm — pigs, chickens and cows"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F3D24]/95 via-[#2F5233]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28 lg:py-36">
          <div className="max-w-2xl text-white">
            <div className="overline text-[#F3C97A] mb-3 sm:mb-5" data-testid="hero-badge">Your Local Online Store</div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight">
              Fresh Meat and Groceries at <span className="text-[#F3C97A] font-extrabold">ARBE</span>
              <span className="block mt-2">Delivered to your door.</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
              Shop our Farm Fresh Meats, Groceries, Condiments and everyday essentials from Arbe Minimart. Pay with GCash, Maya, Bank Transfer, PayPal — or Cash on Delivery.
            </p>
            <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-[#E67E22] hover:bg-[#D35400] text-white rounded-full px-6 sm:px-7 h-11 sm:h-12 text-sm sm:text-base" onClick={() => nav("/shop")} data-testid="hero-shop-now">
                Shop now <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/40 hover:bg-white/20 text-white rounded-full px-6 sm:px-7 h-11 sm:h-12 text-sm sm:text-base" onClick={() => nav("/register")} data-testid="hero-register">
                Create account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
        {[
          { icon: Truck, t: "Same-day delivery", d: "Own driver, fast & reliable." },
          { icon: CircleDollarSign, t: "Pay your way", d: "GCash, Maya, Bank, PayPal, COD." },
          { icon: ShieldCheck, t: "Quality guaranteed", d: "Our farm. Your table. Honest prices." },
          { icon: Store, t: "Walk-in POS", d: "Shop in-store — same system." },
        ].map((v) => (
          <div key={v.t} className="bg-white border border-[#E1E5DF] rounded-2xl p-5 flex items-start gap-3 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
            <div className="h-11 w-11 rounded-xl bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
              <v.icon className="h-5 w-5 text-[#2F5233]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-heading font-semibold text-[#1C2826]">{v.t}</div>
              <div className="text-sm text-[#4A5D5A]">{v.d}</div>
            </div>
          </div>
        ))}
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">Browse by</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1C2826] tracking-tight">Shop by category</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/shop?category=${c.key}`}
              className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-[#E1E5DF] bg-[#F1F3EE]"
              data-testid={`category-${c.key.toLowerCase()}`}
            >
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-0.5">
                <img src={c.images[0]} alt="" className="row-span-2 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <img src={c.images[1]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <img src={c.images[2]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
              <div className="absolute bottom-0 left-0 p-4 sm:p-6 text-white">
                <div className="overline text-[#F3C97A]">Category</div>
                <div className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">{c.key}</div>
                <div className="text-xs sm:text-sm text-white/85">{c.tagline}</div>
              </div>
              <div className="absolute top-4 right-4 bg-white/95 text-[#2F5233] rounded-full h-10 w-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="h-5 w-5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between mb-6 gap-3">
          <div>
            <div className="overline">Featured</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1C2826] tracking-tight">Fresh meats today</h2>
            <div className="text-sm text-[#4A5D5A] mt-1">Pork, beef &amp; chicken — delivered same-day</div>
          </div>
          <Link to="/shop?category=Meats" className="text-sm font-medium text-[#2F5233] hover:underline flex items-center gap-1 flex-shrink-0" data-testid="view-all-products">
            View all meats <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}
